import { apiClient, apiClientBlob, triggerDownload } from "@toss-ads/ui";
import type {
  BreakdownRow,
  TimeseriesBucket,
  TimeseriesPoint,
} from "@toss-ads/ui";
import type { Lead, LeadStatus, PlatformLeadSummary } from "./types";

export interface PaginatedLeads {
  items: Lead[];
  total: number;
  limit: number;
  offset: number;
}

export interface LeadsParams {
  // platform 'organic' = 자연유입, uuid = 특정 플랫폼. (레거시)
  platformId?: string;
  // 매체 필터 키 ('plat:...' | 'ext:...'). platformId 대체.
  media?: string;
  status?: LeadStatus;
  // 'me'(내것) | 'unassigned'(미배정) | 유저 uuid
  assignee?: string;
  // 인입 DB 검색.
  name?: string;
  contact?: string;
  from?: string;
  to?: string;
  isTest?: boolean;
  limit?: number;
  offset?: number;
}

export async function fetchLeads(params: LeadsParams): Promise<PaginatedLeads> {
  const qs = toQuery(params);
  return apiClient<PaginatedLeads>(`/api/leads?${qs}`);
}

// 부재 목록 — 서버가 부재/단선 & 7회 미만으로 강제 필터(status 파라미터 무시).
export async function fetchAbsentLeads(
  params: LeadsParams,
): Promise<PaginatedLeads> {
  const qs = toQuery(params);
  return apiClient<PaginatedLeads>(`/api/leads/absent?${qs}`);
}

export async function fetchPlatformSummaries(): Promise<PlatformLeadSummary[]> {
  return apiClient<PlatformLeadSummary[]>("/api/leads/summaries");
}

export interface MediaOption {
  key: string;
  label: string;
  total: number;
}

export async function fetchStatusTimeseries(params: {
  from?: string;
  to?: string;
  bucket: TimeseriesBucket;
  assignee?: string;
}): Promise<TimeseriesPoint[]> {
  const qs = new URLSearchParams({ bucket: params.bucket });
  if (params.from) qs.set("from", params.from);
  if (params.to) qs.set("to", params.to);
  if (params.assignee) qs.set("assignee", params.assignee);
  return apiClient<TimeseriesPoint[]>(`/api/leads/status-timeseries?${qs}`);
}

export async function fetchStatusBreakdown(params: {
  from?: string;
  to?: string;
  group: "assignee" | "media";
}): Promise<BreakdownRow[]> {
  const qs = new URLSearchParams({ group: params.group });
  if (params.from) qs.set("from", params.from);
  if (params.to) qs.set("to", params.to);
  return apiClient<BreakdownRow[]>(`/api/leads/status-breakdown?${qs}`);
}

export interface ContactTime {
  avgSeconds: number | null;
  sampleCount: number;
}

export async function fetchContactTime(params: {
  from?: string;
  to?: string;
}): Promise<ContactTime> {
  const qs = new URLSearchParams();
  if (params.from) qs.set("from", params.from);
  if (params.to) qs.set("to", params.to);
  return apiClient<ContactTime>(`/api/leads/contact-time?${qs}`);
}

export async function fetchMediaOptions(
  params: { from?: string; to?: string } = {},
): Promise<MediaOption[]> {
  const qs = new URLSearchParams();
  if (params.from) qs.set("from", params.from);
  if (params.to) qs.set("to", params.to);
  const s = qs.toString();
  return apiClient<MediaOption[]>(
    `/api/leads/media-options${s ? `?${s}` : ""}`,
  );
}

export interface SourceDisplay {
  slug: string;
  label: string | null;
}

export async function fetchSources(): Promise<SourceDisplay[]> {
  return apiClient<SourceDisplay[]>("/api/leads/sources");
}

export async function setLeadStatus(id: string, status: LeadStatus): Promise<Lead> {
  return apiClient<Lead>(`/api/leads/${id}/status`, {
    method: "PATCH",
    body: { status },
  });
}

export interface Assignee {
  id: string;
  email: string;
  note: string | null;
  color: string | null;
}

// accountId 미지정 시 내 권한 광고주 전체의 담당자.
export async function fetchAssignees(accountId?: string): Promise<Assignee[]> {
  const qs = accountId ? `?account_id=${accountId}` : "";
  return apiClient<Assignee[]>(`/api/leads/assignees${qs}`);
}

// assigneeId=null 이면 배정 해제.
export async function setLeadAssignee(
  id: string,
  assigneeId: string | null,
): Promise<Lead> {
  return apiClient<Lead>(`/api/leads/${id}/assignee`, {
    method: "PATCH",
    body: { assigneeId },
  });
}

// 연락 시도 1건 삭제 (본인 것만). at = 해당 시도의 ISO 시각.
export async function removeContactAttempt(id: string, at: string): Promise<Lead> {
  return apiClient<Lead>(`/api/leads/${id}/contact-attempts/remove`, {
    method: "PATCH",
    body: { at },
  });
}

// platformKey = 플랫폼 uuid 또는 'organic'(자연유입).
export async function downloadPlatformXlsx(
  platformKey: string,
  params: Omit<LeadsParams, "limit" | "offset" | "platformId"> = {},
): Promise<void> {
  const qs = toQuery(params);
  const { blob, headers } = await apiClientBlob(
    `/api/leads/export/platform/${platformKey}.xlsx${qs ? `?${qs}` : ""}`,
  );
  const cd = headers.get("Content-Disposition") ?? "";
  const match = cd.match(/filename="?([^";]+)"?/);
  const stamp = new Date().toISOString().slice(0, 10).replace(/-/g, "");
  const filename = match?.[1] ?? `leads_${stamp}.xlsx`;
  triggerDownload(blob, filename);
}

function toQuery(params: LeadsParams): string {
  const qs = new URLSearchParams();
  if (params.platformId) qs.set("platform_id", params.platformId);
  if (params.media) qs.set("media", params.media);
  if (params.status) qs.set("status", params.status);
  if (params.assignee) qs.set("assignee", params.assignee);
  if (params.name) qs.set("name", params.name);
  if (params.contact) qs.set("contact", params.contact);
  if (params.from) qs.set("from", params.from);
  if (params.to) qs.set("to", params.to);
  if (params.isTest !== undefined) qs.set("is_test", String(params.isTest));
  if (params.limit !== undefined) qs.set("limit", String(params.limit));
  if (params.offset !== undefined) qs.set("offset", String(params.offset));
  return qs.toString();
}
